import { useState, useCallback } from 'react';

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  variant: ToastVariant;
  title?: string;
  message: string;
  duration: number;
}

export const useToast = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback(
    (variant: ToastVariant, message: string, options?: { title?: string; duration?: number }) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      const newToast: Toast = {
        id,
        variant,
        message,
        title: options?.title,
        duration: options?.duration ?? 3000,
      };

      setToasts(prev => [...prev, newToast]);

      // 自動削除
      if (newToast.duration > 0) {
        setTimeout(() => {
          setToasts(prev => prev.filter(t => t.id !== id));
        }, newToast.duration);
      }

      return id;
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const success = useCallback(
    (message: string, options?: { title?: string; duration?: number }) =>
      showToast('success', message, options),
    [showToast]
  );

  const error = useCallback(
    (message: string, options?: { title?: string; duration?: number }) =>
      showToast('error', message, options),
    [showToast]
  );

  const warning = useCallback(
    (message: string, options?: { title?: string; duration?: number }) =>
      showToast('warning', message, options),
    [showToast]
  );

  const info = useCallback(
    (message: string, options?: { title?: string; duration?: number }) =>
      showToast('info', message, options),
    [showToast]
  );

  return {
    toasts,
    showToast,
    removeToast,
    success,
    error,
    warning,
    info,
  };
};
